import { type ReactNode, useEffect, useRef } from "react";

import { Button, Drawer, Heading } from "@icg360/design-system";

import { hideSupportChat, showSupportChat } from "utils";

import styles from "./mss-drawer.module.scss";

export type MSSDrawerProps = {
  /** Whether the drawer is open */
  isOpen: boolean;
  /** Called when the drawer should close */
  onClose: () => void;
  /** Called when the drawer opens */
  onOpen?: () => void;
  /** Whether to show the backdrop. Defaults to true */
  showBackdrop?: boolean;
  /** Drawer content */
  children: ReactNode;
};

export const MSSDrawer = ({
  isOpen,
  onClose,
  onOpen,
  showBackdrop = true,
  children,
}: MSSDrawerProps) => {
  const savedScrollYRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      savedScrollYRef.current = scrollY;

      hideSupportChat();

      const originalPosition = document.body.style.position;
      const originalTop = document.body.style.top;
      const originalLeft = document.body.style.left;
      const originalRight = document.body.style.right;

      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";

      return () => {
        showSupportChat();
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.left = originalLeft;
        document.body.style.right = originalRight;
        window.scrollTo(0, savedScrollYRef.current);
      };
    }
    showSupportChat();
    return () => {};
  }, [isOpen]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      showBackdrop={showBackdrop}
    >
      <div className={styles.content}>{children}</div>
    </Drawer>
  );
};

export type MSSDrawerHeaderProps = {
  /** Drawer title */
  title: string;
  /** Called when close is clicked. Close button is always shown. */
  onClose?: () => void;
  /** Optional data-testid for the header close button */
  closeButtonTestId?: string;
};

export const MSSDrawerHeader = ({
  title,
  onClose,
  closeButtonTestId,
}: MSSDrawerHeaderProps) => (
  <div className={styles.header}>
    <div className={styles.headerInner}>
      <Heading size="md" tag="h2" className={styles.heading}>
        {title}
      </Heading>
    </div>
    <Button
      className={styles.closeButton}
      onPress={() => onClose?.()}
      size="sm"
      icon="Close"
      appearance="neutral"
      aria-label="Close"
      {...(closeButtonTestId && { "data-testid": closeButtonTestId })}
    />
  </div>
);

export type MSSDrawerBodyProps = {
  /** Drawer body content. Use your own div or any content here. */
  children: ReactNode;
};

export const MSSDrawerBody = ({ children }: MSSDrawerBodyProps) => (
  <div className={styles.body}>{children}</div>
);

export type MSSDrawerFooterProps = {
  /** Button text for the primary (main) action */
  primaryLabel: string;
  /** Your custom function; called when the primary button is pressed */
  onPrimary: () => void;
  /** Optional: disable the primary button (e.g. when form is invalid) */
  primaryDisabled?: boolean;
  /** Optional: show loading state on the primary button (e.g. while submitting) */
  primaryLoading?: boolean;
  /** Optional button text for the secondary action (e.g. Cancel). Omit to show only the primary button. */
  secondaryLabel?: string;
  /** Your custom function; called when the secondary button is pressed. Ignored if secondaryLabel is not provided. */
  onSecondary?: () => void;
  /** Optional: disable the secondary button */
  secondaryDisabled?: boolean;
  /** Optional: show loading state on the secondary button */
  secondaryLoading?: boolean;
  /** Optional data-testid for the primary footer button */
  primaryButtonTestId?: string;
  /** Optional data-testid for the secondary footer button */
  secondaryButtonTestId?: string;
};

export const MSSDrawerFooter = ({
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryLoading = false,
  secondaryLabel,
  onSecondary,
  secondaryDisabled = false,
  secondaryLoading = false,
  primaryButtonTestId,
  secondaryButtonTestId,
}: MSSDrawerFooterProps) => (
  <div className={styles.footer}>
    {secondaryLabel != null && secondaryLabel !== "" ? (
      <div className={styles.footerButton}>
        <Button
          appearance="secondary"
          onPress={onSecondary ?? (() => {})}
          disabled={secondaryDisabled}
          loading={secondaryLoading}
          {...(secondaryButtonTestId && {
            "data-testid": secondaryButtonTestId,
          })}
        >
          {secondaryLabel}
        </Button>
      </div>
    ) : null}
    <div className={styles.footerButton}>
      <Button
        appearance="primary"
        onPress={onPrimary}
        disabled={primaryDisabled}
        loading={primaryLoading}
        {...(primaryButtonTestId && { "data-testid": primaryButtonTestId })}
      >
        {primaryLabel}
      </Button>
    </div>
  </div>
);

// Attach subcomponents so consumers can use MSSDrawer.Header / MSSDrawer.Body / MSSDrawer.Footer
MSSDrawer.Header = MSSDrawerHeader;
MSSDrawer.Body = MSSDrawerBody;
MSSDrawer.Footer = MSSDrawerFooter;
